import { ChainId } from '@xspswap/smart-order-router'
import {
  LARGE_MEDIA_BREAKPOINT,
  MAX_WIDTH_MEDIA_BREAKPOINT,
  MEDIUM_MEDIA_BREAKPOINT,
  SMALL_MEDIA_BREAKPOINT,
} from 'components/Tokens/constants'
import { format } from 'date-fns'
import { darken } from 'polished'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode, useMemo } from 'react'
import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components/macro'

import CurrencyLogo from '../../../components/Logo/CurrencyLogo'
import { MouseoverTooltip } from '../../../components/Tooltip'
import { PoolAndPairQuery } from '../../../graphql/competition/__generated__/types-and-hooks'
import { Chain } from '../../../graphql/data/__generated__/types-and-hooks'
import { getTokenDetailsURL } from '../../../graphql/data/util'
import { getNowSeconds } from '../../../utils/getNowSeconds'

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const StyledTokenRow = styled.div<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  background-color: transparent;
  display: grid;
  font-size: 16px;
  grid-template-columns: 1fr 30fr;
  line-height: 24px;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  ${({ first, last }) => css`
    height: ${first || last ? '72px' : '64px'};
    padding-top: ${first ? '8px' : '0px'};
    padding-bottom: ${last ? '8px' : '0px'};
  `}
  padding-left: 12px;
  padding-right: 12px;
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  width: 100%;
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }

  @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 22.3fr;
  }

  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 22.7fr;
  }

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 21.2fr;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    /* grid-template-columns: 10fr; */
    /* min-width: unset; */
    border-bottom: 0.5px solid ${({ theme }) => theme.backgroundModule};
    :last-of-type {
      border-bottom: none;
    }
  }
`

const ClickableContent = styled.div`
  display: flex;
  text-decoration: none;
  color: ${({ theme }) => theme.textPrimary};
  align-items: center;
  cursor: pointer;
`
const ClickableName = styled(ClickableContent)`
  gap: 8px;
  width: 100%;
`
const StyledHeaderRow = styled(StyledTokenRow)`
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px 8px 0px 0px;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 14px;
  height: 48px;
  line-height: 16px;
  padding: 0px 12px;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

const ListNumberCell = styled(Cell)<{ header: boolean }>`
  color: ${({ theme }) => theme.textTertiary};
  min-width: 32px;
  font-size: 14px;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const NameCell = styled(Cell)`
  justify-content: flex-start;
  min-width: 240px;
  padding: 0px 8px;
  gap: 8px;
`

const StyledLink = styled(Link)`
  text-decoration: none;
`
const TokenInfoCell = styled(Cell)`
  gap: 8px;
  line-height: 24px;
  font-size: 16px;
  max-width: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: flex-start;
    flex-direction: column;
    gap: 0px;
    width: max-content;
    font-weight: 500;
  }
`

const TokenName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`
const TokenSymbolMobile = styled(Cell)`
  display: none;
  color: ${({ theme }) => theme.textTertiary};
  text-transform: uppercase;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: flex;
    font-size: 12px;
    height: 16px;
    justify-content: flex-start;
    width: 100%;
  }
`

const TokenSymbolDesktop = styled.span`
  color: ${({ theme }) => theme.textTertiary};
  text-transform: uppercase;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
    /* font-size: 12px;
    height: 16px;
    justify-content: flex-start;
    width: 100%; */
  }
`

export const blinkAnimation = keyframes`
  100% {
    box-shadow: none;
  }
`

const MultiplierWrapper = styled.div`
  background: ${({ theme }) => theme.accentCritical};
  box-shadow: 0 0 4px 1px ${({ theme }) => darken(0.09, theme.accentCritical)};
  padding: 0 6px;
  height: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  font-size: 13px;
  border-radius: 8px;
  font-weight: bold;
  animation: ${blinkAnimation} 1.5s infinite;
`

/* Token Row: skeleton row component */
function TokenRow({
  header,
  listNumber,
  tokenInfo,
  ...rest
}: {
  first?: boolean
  header: boolean
  listNumber?: ReactNode
  loading?: boolean
  tokenInfo: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <ListNumberCell header={header}>{listNumber}</ListNumberCell>
      <NameCell>{tokenInfo}</NameCell>
    </>
  )
  if (header) return <StyledHeaderRow data-testid="header-row">{rowCells}</StyledHeaderRow>
  return <StyledTokenRow {...rest}>{rowCells}</StyledTokenRow>
}

interface LoadedTokenRowProps {
  tokenListIndex: number
  tokenListLength: number
  sortRank: number
  data: PoolAndPairQuery['tokens'][number]
}

/* Loaded State: row component with token information */
export const TrackedTokenRow = forwardRef(
  (
    {
      tokenListIndex,
      tokenListLength,
      sortRank,
      data: { id, symbol, name, multiplier, multiplierStart, multiplierEnd },
    }: LoadedTokenRowProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const multi = useMemo(() => {
      const secs = getNowSeconds()
      let start,
        end = 0
      let m = 100
      try {
        start = Number(multiplierStart)
        end = Number(multiplierEnd)
        m = Number(multiplier)
      } catch (e) {
        console.log(e)
        return null
      }
      if (m > 100 && secs > start && secs < end) {
        return (
          <MouseoverTooltip
            text={
              <div>
                <div style={{ fontSize: '15px', marginBottom: '10px' }}>Token bonus multiplier</div>
                <div>Start: {format(multiplierStart * 1000, 'dd MMM yyyy HH:mm')}UTC</div>
                <div>End: {format(multiplierEnd * 1000, 'dd MMM yyyy HH:mm')}UTC</div>
              </div>
            }
            placement="top"
          >
            <MultiplierWrapper>x {m / 100}</MultiplierWrapper>
          </MouseoverTooltip>
        )
      }
      return null
    }, [multiplier, multiplierStart, multiplierEnd])
    return (
      <div ref={ref} data-testid={`token-table-row-${id}`}>
        <StyledLink to={getTokenDetailsURL({ address: id, chain: Chain.Xinfin })}>
          <TokenRow
            header={false}
            listNumber={sortRank}
            tokenInfo={
              <ClickableName>
                <CurrencyLogo
                  currency={{
                    chainId: ChainId.XDC,
                    symbol,
                    decimals: 18,
                    name,
                    // @ts-ignore
                    wrapped: {
                      address: id,
                    },
                    address: id,
                    isNative: false,
                    isToken: true,
                  }}
                  size="24px"
                  margin={true}
                />

                <TokenInfoCell>
                  <TokenName data-cy="token-name">
                    {name} <TokenSymbolDesktop>({symbol})</TokenSymbolDesktop>
                  </TokenName>
                  <TokenSymbolMobile>{symbol}</TokenSymbolMobile>
                </TokenInfoCell>
                {multi}
              </ClickableName>
            }
            first={tokenListIndex === 0}
            last={tokenListIndex === tokenListLength - 1}
          />
        </StyledLink>
      </div>
    )
  }
)

TrackedTokenRow.displayName = 'TrackedTokenRow'
